import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Music, Utensils, Star, MapPin, Users, Clock, RefreshCw } from 'lucide-react';
import { format, isPast, addDays, getDay } from 'date-fns';
import { pl } from 'date-fns/locale';
import { API_BASE_URL } from '../config/environment';

const getNextOccurrence = (recurringDays) => {
  if (!Array.isArray(recurringDays) || recurringDays.length === 0) return new Date();
  const today = new Date();
  const todayDay = getDay(today);
  let minDays = 8;
  for (const day of recurringDays) {
    let diff = day - todayDay;
    if (diff <= 0) diff += 7;
    if (diff < minDays) minDays = diff;
  }
  return addDays(today, minDays);
};

const DAY_NAMES = {
  pl: ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
};

const formatRecurringDays = (days, lang) => {
  if (!Array.isArray(days) || days.length === 0) return '';
  const names = DAY_NAMES[lang] || DAY_NAMES['pl'];
  return [...days].sort((a, b) => a - b).map(d => names[d]).join(', ');
};

const EventsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('upcoming');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/events`);
        if (!response.ok) {
          throw new Error('Błąd podczas pobierania wydarzeń');
        }
        const data = await response.json();
        const sortedEvents = data.events.sort((a, b) => new Date(a.date) - new Date(b.date));
        setEvents(sortedEvents);
      } catch (err) {
        setError(t('events.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [t]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'music':
        return <Music className="w-6 h-6" />;
      case 'cooking':
        return <Utensils className="w-6 h-6" />;
      case 'tasting':
        return <Star className="w-6 h-6" />;
      default:
        return <Calendar className="w-6 h-6" />;
    }
  };

  const handleRegister = async (eventId) => {
    console.log('Rejestracja na wydarzenie:', eventId);
  };

  const lang = i18n.language?.substring(0, 2) || 'pl';

  const filteredEvents = events.filter(event => {
    const isRecurring = event.isRecurring === true || event.isRecurring === 'true';
    if (filter === 'upcoming') {
      if (isRecurring) return true; // recurring events are always "upcoming"
      return !isPast(new Date(event.date));
    }
    return true;
  });

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-secondary flex items-center justify-center">
        <div className="animate-pulse flex space-x-2 text-xl text-cream">
          <span className="w-2 h-2 bg-cream rounded-full animate-bounce"></span>
          <span className="w-2 h-2 bg-cream rounded-full animate-bounce delay-100"></span>
          <span className="w-2 h-2 bg-cream rounded-full animate-bounce delay-200"></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen bg-secondary flex items-center justify-center">
        <div className="text-xl text-primary bg-cream/10 px-6 py-4 rounded-lg shadow-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-secondary">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col items-center mb-12">
          <h1 className="text-4xl font-bold text-center mb-6 text-cream">{t('events.title')}</h1>
          
          {/* Nowy tekst wprowadzający */}
          <p className="text-cream/90 text-xl text-center max-w-3xl mb-10 leading-relaxed">
            Tortuga to miejsce, gdzie codzienność ustępuje miejsca emocjom, rozrywce i niezapomnianym chwilom. 
            Śledź nasz kalendarz wydarzeń, aby nie przegapić kolejnych wieczorów pełnych magii i dobrej energii!
          </p>

          <div className="flex space-x-4">
            <button
              onClick={() => setFilter('upcoming')}
              className={`px-6 py-2.5 rounded-lg transition-all duration-200 transform hover:scale-105 
                ${filter === 'upcoming'
                  ? 'bg-primary text-cream shadow-lg ring-2 ring-primary/50'
                  : 'bg-secondary text-cream hover:bg-primaryDark ring-1 ring-cream/20'
                }`}
            >
              {t('events.upcomingEvents')}
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2.5 rounded-lg transition-all duration-200 transform hover:scale-105
                ${filter === 'all'
                  ? 'bg-primary text-cream shadow-lg ring-2 ring-primary/50'
                  : 'bg-secondary text-cream hover:bg-primaryDark ring-1 ring-cream/20'
                }`}
            >
              {t('events.allEvents')}
            </button>
          </div>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="text-center text-cream py-12 bg-secondary/50 rounded-lg">
            {filter === 'upcoming' ? t('events.noUpcomingEvents') : t('events.noEvents')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEvents.map((event) => {
              const isRecurring = event.isRecurring === true || event.isRecurring === 'true';
              const recurringDays = isRecurring ? (event.recurringDays || (event.recurringDay !== undefined ? [event.recurringDay] : [])) : [];
              const eventDate = isRecurring
                ? getNextOccurrence(recurringDays)
                : (event.date ? new Date(event.date) : null);
              const isEventPast = isRecurring ? false : (eventDate ? isPast(eventDate) : false);
              const isFull = event.maxParticipants ? event.maxParticipants <= (event.participants?.length || 0) : false;
              const recurringDaysText = isRecurring ? formatRecurringDays(recurringDays, lang) : '';
              const timeDisplay = event.timeFrom
                ? (event.timeTo ? `${event.timeFrom} – ${event.timeTo}` : event.timeFrom)
                : event.time || null;

              return (
                <div 
                  key={event.id} 
                  className={`bg-cream rounded-lg shadow-lg overflow-hidden transition-all duration-300 
                    hover:shadow-xl hover:translate-y-[-4px] cursor-pointer ${isEventPast ? 'opacity-75' : ''}`}
                  onClick={() => event.slug ? navigate(`/events/${event.slug}`) : null}
                >
                  <div className="relative">
                    <img
                      src={event.image ? `${API_BASE_URL}${event.image}` : '/api/placeholder/400/250'}
                      alt={event.title}
                      className="w-full h-48 object-cover"
                    />
                    {isRecurring && (
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 
                        bg-[#D4AF37]/90 text-[#1A2F23] text-xs font-semibold rounded-full backdrop-blur-sm">
                        <RefreshCw className="w-3 h-3" />
                        {recurringDaysText}
                      </div>
                    )}
                    {isEventPast && (
                      <div className="absolute inset-0 bg-secondary/70 backdrop-blur-sm flex items-center justify-center">
                        <span className="text-cream text-lg font-semibold px-4 py-2 bg-secondary/50 rounded-lg">
                          {t('events.eventEnded')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-primary p-2 bg-primary/10 rounded-lg">
                        {getCategoryIcon(event.category)}
                      </span>
                      <span className="text-sm font-medium text-secondary">
                        {t(`events.categories.${event.category}`)}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-secondaryLight">{event.title}</h3>
                    <p className="text-warmGray line-clamp-2">{event.description}</p>
                    
                    <div className="space-y-3 divide-y divide-secondary/10">
                      <div className="grid gap-2 pb-3">
                        {eventDate && (
                          <div className="flex items-center text-sm text-warmGray">
                            <Calendar className="w-4 h-4 mr-2 text-primary" />
                            <span>
                              {isRecurring
                                ? `${t('events.nextDate', 'Najbliższy termin')}: ${format(eventDate, 'd MMMM yyyy', { locale: pl })}`
                                : format(eventDate, 'd MMMM yyyy', { locale: pl })}
                            </span>
                          </div>
                        )}
                        {timeDisplay && (
                          <div className="flex items-center text-sm text-warmGray">
                            <Clock className="w-4 h-4 mr-2 text-primary" />
                            <span>{timeDisplay}</span>
                          </div>
                        )}
                        {event.location && (
                          <div className="flex items-center text-sm text-warmGray">
                            <MapPin className="w-4 h-4 mr-2 text-primary" />
                            <span>{event.location}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="pt-3 space-y-3">
                        {event.maxParticipants && (
                          <div className="flex items-center text-sm text-warmGray">
                            <Users className="w-4 h-4 mr-2 text-primary" />
                            <span>
                              {event.maxParticipants - (event.participants?.length || 0)}/
                              {event.maxParticipants} {t('events.availablePlaces')}
                            </span>
                          </div>
                        )}
                        {event.price && (
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-bold text-primary">{event.price} zł</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (event.slug) {
                          navigate(`/events/${event.slug}`);
                        } else {
                          handleRegister(event.id);
                        }
                      }}
                      className={`w-full py-3 px-4 rounded-lg transition-all duration-200 
                        transform hover:scale-105 active:scale-95 font-medium
                        ${isEventPast || isFull
                          ? 'bg-warmGray text-cream cursor-not-allowed'
                          : 'bg-primary text-cream hover:bg-primaryDark shadow-lg hover:shadow-xl'
                        }`}
                      disabled={isEventPast || isFull}
                    >
                      {isEventPast
                        ? t('events.eventEnded')
                        : isFull
                        ? t('events.noSlotsAvailable')
                        : event.slug
                        ? t('events.moreInfo', 'Więcej informacji')
                        : t('events.registerButton')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;